import type { ComponentDefinition } from "../types";

export const MONITOR: ComponentDefinition[] = [
  {
    id: "monitor-magnum-me-arc50",
    name: "Magnum Energy ME-ARC50 Advanced Remote Control",
    shortName: "ME-ARC50",
    subtitle: "Inverter/charger remote",
    category: "monitor",
    manufacturer: "Magnum Energy",
    model: "ME-ARC50",
    width: 160,
    height: 90,
    // ME-ARC manual 64-0030 Rev F, pp1/3/7: 50 ft twisted-pair 6P4C
    // telephone-style cable; powered by the inverter through this cable.
    // No separate battery input or published independent DC consumption.
    // https://www.magnum-dimensions.com/sites/default/files/manuals/owners/64-0030%20Rev%20F%20%28ME-ARC%29_0.pdf
    terminals: [
      { id: "remote", label: "REMOTE RJ11", polarity: "signal", direction: "bidi", side: "left", offset: 0.5, maxAmp: 0, color: "#22c55e" },
    ],
    image: "monitor-magnum-me-arc50.webp",
  },
  {
    id: "mon-bmv-712",
    name: "Victron BMV-712 Smart",
    shortName: "BMV-712",
    subtitle: "Battery monitor 500A, dual",
    category: "monitor",
    manufacturer: "Victron",
    model: "BMV-712",
    width: 100,
    height: 60,
    terminals: [
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.3,
        maxAmp: 500,
      },
      {
        id: "load-neg",
        label: "LOAD −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.3,
        maxAmp: 500,
      },
      {
        id: "vbat",
        label: "B+ sense",
        polarity: "signal",
        direction: "bidi",
        side: "right",
        offset: 0.6,
        maxAmp: 1,
      },
      {
        id: "vaux",
        label: "AUX sense",
        polarity: "signal",
        direction: "bidi",
        side: "right",
        offset: 0.85,
        maxAmp: 1,
      },
    ],
    image: "mon-bmv-712.webp",
  },
  {
    id: "mon-smartshunt-300",
    name: "Victron SmartShunt 300A",
    shortName: "SmartShunt 300",
    subtitle: "Battery shunt 300A",
    category: "monitor",
    manufacturer: "Victron",
    model: "SmartShunt 300A",
    width: 100,
    height: 60,
    terminals: [
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.5,
        maxAmp: 300,
      },
      {
        id: "load-neg",
        label: "LOAD −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.3,
        maxAmp: 300,
      },
      {
        id: "vbat",
        label: "B+ sense",
        polarity: "signal",
        direction: "bidi",
        side: "right",
        offset: 0.7,
        maxAmp: 1,
      },
    ],
    image: "mon-smartshunt-300.webp",
  },
  {
    id: "mon-smartshunt-500",
    name: "Victron SmartShunt 500A",
    shortName: "SmartShunt 500",
    subtitle: "Battery shunt 500A",
    category: "monitor",
    manufacturer: "Victron",
    model: "SmartShunt 500A/50mV (IP21)",
    width: 100,
    height: 60,
    // Victron SmartShunt installation §§3.3–3.6: Vbatt+, one configurable
    // Aux input and VE.Direct, in addition to the two shunt studs.
    // https://www.victronenergy.com/media/pg/SmartShunt/en/installation.html
    terminals: [
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.5,
        maxAmp: 500,
      },
      {
        id: "load-neg",
        label: "LOAD −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.3,
        maxAmp: 500,
      },
      {
        id: "vbat",
        label: "Vbatt+ power",
        // Required fused supply; retain sense semantics so it cannot carry load current.
        color: "#c43131",
        polarity: "signal",
        direction: "bidi",
        side: "right",
        offset: 0.7,
        maxAmp: 1,
      },
      {
        id: "vaux",
        label: "AUX sense",
        polarity: "signal",
        direction: "bidi",
        side: "bottom",
        offset: 0.85,
        maxAmp: 1,
      },
      {
        id: "ve-direct",
        label: "VE.Direct",
        polarity: "signal",
        direction: "bidi",
        side: "bottom",
        offset: 0.15,
        maxAmp: 0,
      },
    ],
    image: "mon-smartshunt-500.webp",
  },
  {
    id: "mon-smartshunt-1000",
    name: "Victron SmartShunt 1000A",
    shortName: "SmartShunt 1000",
    subtitle: "Battery shunt 1000A",
    category: "monitor",
    manufacturer: "Victron",
    model: "SmartShunt 1000A",
    width: 100,
    height: 60,
    terminals: [
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.5,
        maxAmp: 1000,
      },
      {
        id: "load-neg",
        label: "LOAD −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.3,
        maxAmp: 1000,
      },
      {
        id: "vbat",
        label: "B+ sense",
        polarity: "signal",
        direction: "bidi",
        side: "right",
        offset: 0.7,
        maxAmp: 1,
      },
    ],
    image: "mon-smartshunt-1000.webp",
  },
  {
    id: "mon-smartshunt-2000",
    name: "Victron SmartShunt 2000A",
    shortName: "SmartShunt 2000",
    subtitle: "Battery shunt 2000A",
    category: "monitor",
    manufacturer: "Victron",
    model: "SmartShunt 2000A",
    width: 100,
    height: 60,
    terminals: [
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.5,
        maxAmp: 2000,
      },
      {
        id: "load-neg",
        label: "LOAD −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.3,
        maxAmp: 2000,
      },
      {
        id: "vbat",
        label: "B+ sense",
        polarity: "signal",
        direction: "bidi",
        side: "right",
        offset: 0.7,
        maxAmp: 1,
      },
    ],
    image: "mon-smartshunt-2000.webp",
  },
  {
    id: "mon-cerbo-gx",
    name: "Victron Cerbo GX",
    shortName: "Cerbo GX",
    subtitle: "System gateway",
    category: "monitor",
    manufacturer: "Victron",
    model: "Cerbo GX",
    width: 200,
    height: 110,
    terminals: [
      {
        id: "pwr-pos",
        label: "+12V",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 1,
      },
      {
        id: "pwr-neg",
        label: "GND",
        polarity: "ground",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 1,
      },
    ],
    ampDraw: 0.25,
    image: "mon-cerbo-gx.webp",
  },
  {
    id: "mon-balmar-sg200",
    name: "Balmar SG200",
    shortName: "SG200",
    subtitle: "Battery monitor 350A",
    category: "monitor",
    manufacturer: "Balmar",
    model: "SG200",
    // Balmar SG200 datasheet: 350 A max continuous shunt.
    width: 100,
    height: 60,
    terminals: [
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.5,
        maxAmp: 350,
      },
      {
        id: "load-neg",
        label: "LOAD −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.3,
        maxAmp: 350,
      },
      {
        id: "vbat",
        label: "B+ sense",
        polarity: "signal",
        direction: "bidi",
        side: "right",
        offset: 0.7,
        maxAmp: 1,
      },
    ],
    image: "mon-balmar-sg200.webp",
  },
  {
    id: "mon-renogy-500a",
    name: "Renogy Battery Monitor 500A",
    shortName: "Battery Monitor 500A",
    subtitle: "Battery monitor 500A",
    category: "monitor",
    manufacturer: "Renogy",
    model: "RBM500",
    width: 100,
    height: 60,
    terminals: [
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.5,
        maxAmp: 500,
      },
      {
        id: "load-neg",
        label: "LOAD −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.3,
        maxAmp: 500,
      },
      {
        id: "vbat",
        label: "B+ sense",
        polarity: "signal",
        direction: "bidi",
        side: "right",
        offset: 0.7,
        maxAmp: 1,
      },
    ],
    image: "mon-renogy-500a.webp",
  },
  {
    id: "mon-victron-lynx-shunt-vecan",
    name: "Victron Lynx Shunt VE.Can",
    shortName: "Lynx Shunt",
    subtitle: "1000A shunt + main fuse",
    category: "monitor",
    manufacturer: "Victron",
    model: "Lynx Shunt VE.Can",
    width: 220,
    height: 140,
    terminals: [
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.3,
        maxAmp: 1000,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 1000,
      },
      {
        id: "bus-pos",
        label: "BUS +",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.3,
        maxAmp: 1000,
      },
      {
        id: "bus-neg",
        label: "BUS −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.7,
        maxAmp: 1000,
      },
    ],
    image: "mon-victron-lynx-shunt-vecan.webp",
  },
  {
    id: "mon-victron-gx-touch-50",
    name: "Victron GX Touch 50",
    shortName: "GX Touch 50",
    subtitle: '5" touchscreen for GX',
    category: "monitor",
    manufacturer: "Victron",
    model: "GX Touch 50",
    width: 170,
    height: 100,
    terminals: [],
    image: "mon-victron-gx-touch-50.webp",
  },
  {
    id: "mon-victron-gx-touch-70",
    name: "Victron GX Touch 70",
    shortName: "GX Touch 70",
    subtitle: '7" touchscreen for GX',
    category: "monitor",
    manufacturer: "Victron",
    model: "GX Touch 70",
    width: 195,
    height: 115,
    terminals: [],
    image: "mon-victron-gx-touch-70.webp",
  },
  {
    id: "mon-victron-ekrano-gx",
    name: "Victron Ekrano GX",
    shortName: "Ekrano GX",
    subtitle: 'GX device + 7" display',
    category: "monitor",
    manufacturer: "Victron",
    model: "Ekrano GX",
    width: 200,
    height: 120,
    terminals: [
      {
        id: "pwr-pos",
        label: "V+ (8–70V)",
        polarity: "positive",
        direction: "input",
        // Modeling ceiling follows the supplied fused lead, not a connector rating.
        maxAmp: 3.15,
        side: "left",
        offset: 0.4,
      },
      {
        id: "pwr-neg",
        label: "GND",
        polarity: "ground",
        direction: "input",
        // Modeling ceiling follows the supplied fused lead, not a connector rating.
        maxAmp: 3.15,
        side: "left",
        offset: 0.7,
      },
    ],
    ampDraw: 0.52,
    image: "mon-victron-ekrano-gx.webp",
  },
  {
    id: "mon-victron-venus-gx",
    name: "Victron Venus GX",
    shortName: "Venus GX",
    subtitle: "System gateway",
    category: "monitor",
    manufacturer: "Victron",
    model: "Venus GX",
    width: 185,
    height: 105,
    terminals: [
      {
        id: "pwr-pos",
        label: "V+ (8–70V)",
        polarity: "positive",
        direction: "input",
        // Modeling ceiling follows the supplied fused lead, not a connector rating.
        maxAmp: 3.15,
        side: "left",
        offset: 0.4,
      },
      {
        id: "pwr-neg",
        label: "GND",
        polarity: "ground",
        direction: "input",
        // Modeling ceiling follows the supplied fused lead, not a connector rating.
        maxAmp: 3.15,
        side: "left",
        offset: 0.7,
      },
    ],
    ampDraw: 0.21,
    image: "mon-victron-venus-gx.webp",
  },
  {
    id: "mon-bmv-700",
    name: "Victron BMV-700",
    shortName: "BMV-700",
    subtitle: "Battery monitor 500A",
    category: "monitor",
    manufacturer: "Victron",
    model: "BMV-700",
    width: 100,
    height: 60,
    terminals: [
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.3,
        maxAmp: 500,
      },
      {
        id: "load-neg",
        label: "LOAD −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.3,
        maxAmp: 500,
      },
      {
        id: "vbat",
        label: "B+ sense",
        polarity: "signal",
        direction: "bidi",
        side: "right",
        offset: 0.7,
        maxAmp: 1,
      },
    ],
    image: "mon-bmv-700.webp",
  },
  {
    id: "mon-bmv-702",
    name: "Victron BMV-702",
    shortName: "BMV-702",
    subtitle: "Battery monitor 500A + auxiliary voltage",
    category: "monitor",
    manufacturer: "Victron",
    model: "BMV-702",
    width: 100,
    height: 60,
    terminals: [
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.3,
        maxAmp: 500,
      },
      {
        id: "load-neg",
        label: "LOAD −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.3,
        maxAmp: 500,
      },
      {
        id: "vbat",
        label: "B+ sense",
        polarity: "signal",
        direction: "bidi",
        side: "right",
        offset: 0.6,
        maxAmp: 1,
      },
      {
        id: "vaux",
        label: "AUX sense",
        polarity: "signal",
        direction: "bidi",
        side: "right",
        offset: 0.85,
        maxAmp: 1,
      },
    ],
    image: "mon-bmv-702.webp",
  },
  {
    id: "mon-smartshunt-ip65-500",
    name: "Victron SmartShunt IP65 500A",
    shortName: "SS IP65 500",
    subtitle: "Battery shunt 500A, IP65",
    category: "monitor",
    manufacturer: "Victron",
    model: "SmartShunt IP65 500A",
    width: 100,
    height: 60,
    terminals: [
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.5,
        maxAmp: 500,
      },
      {
        id: "load-neg",
        label: "LOAD −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.3,
        maxAmp: 500,
      },
      {
        id: "vbat",
        label: "B+ sense",
        polarity: "signal",
        direction: "bidi",
        side: "right",
        offset: 0.7,
        maxAmp: 1,
      },
    ],
    image: "mon-smartshunt-ip65-500.webp",
  },
  {
    id: "mon-victron-smart-battery-sense",
    name: "Victron Smart Battery Sense",
    shortName: "Battery Sense",
    subtitle: "Wireless V/T sender",
    category: "monitor",
    manufacturer: "Victron",
    model: "Smart Battery Sense",
    width: 130,
    height: 70,
    terminals: [
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.35,
        maxAmp: 1,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 1,
      },
    ],
    ampDraw: 0.0016,
    image: "mon-victron-smart-battery-sense.webp",
  },
  {
    id: "mon-victron-globallink-520",
    name: "Victron GlobalLink 520",
    shortName: "GlobalLink 520",
    subtitle: "Cellular VRM uplink",
    category: "monitor",
    manufacturer: "Victron",
    model: "GlobalLink 520",
    width: 165,
    height: 90,
    terminals: [
      {
        id: "pwr-pos",
        label: "V+ (8–70V)",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 1,
      },
      {
        id: "pwr-neg",
        label: "GND",
        polarity: "ground",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 1,
      },
    ],
    ampDraw: 0.05,
    image: "mon-victron-globallink-520.webp",
  },
  // Manufacturer evidence and model boundaries: docs/research/component-manuals-common-50-2026-10-01.md.
  {
    "id": "mon-nasa-bm1-compact",
    "name": "NASA Marine BM-1 Compact 12V",
    "shortName": "BM-1 Compact",
    "subtitle": "Lead-acid monitor + shunt",
    "category": "monitor",
    "manufacturer": "NASA Marine",
    "model": "BM-1 Compact 12V",
    "voltage": 12,
    "compatibleVoltages": [
      12
    ],
    "width": 210,
    "height": 105,
    "terminals": [
      {
        "id": "batt-neg",
        "label": "BATT −",
        "polarity": "negative",
        "direction": "input",
        "maxAmp": 100,
        "side": "left",
        "offset": 0.5
      },
      {
        "id": "load-neg",
        "label": "SYSTEM −",
        "polarity": "negative",
        "direction": "output",
        "maxAmp": 100,
        "side": "right",
        "offset": 0.3
      },
      {
        "id": "vbat",
        "label": "FUSED B+",
        "polarity": "signal",
        "direction": "input",
        "maxAmp": 0,
        "side": "right",
        "offset": 0.75
      }
    ]
  },
  {
    "id": "mon-simarine-pico-one",
    "name": "Simarine PICO One Set",
    "shortName": "PICO + SC303",
    "subtitle": "Battery / tank monitor set",
    "category": "monitor",
    "manufacturer": "Simarine",
    "model": "PICO-ONE (PICO + SC303)",
    "compatibleVoltages": [
      12,
      24
    ],
    "width": 220,
    "height": 125,
    "terminals": [
      {
        "id": "batt-neg",
        "label": "SHUNT IN −",
        "polarity": "negative",
        "direction": "input",
        "maxAmp": 300,
        "side": "left",
        "offset": 0.5
      },
      {
        "id": "load-neg",
        "label": "SHUNT OUT −",
        "polarity": "negative",
        "direction": "output",
        "maxAmp": 300,
        "side": "right",
        "offset": 0.25
      },
      {
        "id": "vbat",
        "label": "V1 B+ SENSE",
        "polarity": "signal",
        "direction": "input",
        "maxAmp": 0,
        "side": "right",
        "offset": 0.55
      },
      {
        "id": "vaux",
        "label": "V2 AUX SENSE",
        "polarity": "signal",
        "direction": "input",
        "maxAmp": 0,
        "side": "right",
        "offset": 0.85
      }
    ]
  },
  {
    "id": "mon-cbe-pc180",
    "name": "CBE PC180 Control Panel",
    "shortName": "PC180",
    "subtitle": "Control panel requiring DS300",
    "manufacturer": "CBE",
    "model": "PC180",
    "category": "monitor",
    "width": 230,
    "height": 110,
    "terminals": [
      {
        "id": "panel-harness",
        "label": "DS300 panel harness",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "left",
        "offset": 0.5
      }
    ]
  },
  // Additional manufacturer-reviewed models; evidence: docs/research/component-manuals-additional-50-2026-10-01.md
  {
    "id": "mon-victron-battery-balancer",
    "image": "mon-victron-battery-balancer.webp",
    "name": "Victron Battery Balancer",
    "shortName": "Battery Balancer",
    "subtitle": "Midpoint balancer for two series-connected 12 V batteries",
    "category": "monitor",
    "manufacturer": "Victron",
    "model": "Battery Balancer",
    "terminals": [
      {
        "id": "bank-pos",
        "label": "BANK +",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "left",
        "offset": 0.2
      },
      {
        "id": "midpoint",
        "label": "MIDPOINT",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "left",
        "offset": 0.5
      },
      {
        "id": "bank-neg",
        "label": "BANK −",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "left",
        "offset": 0.8
      },
      {
        "id": "alarm-a",
        "label": "ALARM",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 1,
        "side": "right",
        "offset": 0.25
      },
      {
        "id": "alarm-b",
        "label": "ALARM",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 1,
        "side": "right",
        "offset": 0.5
      },
      {
        "id": "reset-a",
        "label": "RESET",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "bottom",
        "offset": 0.3
      },
      {
        "id": "reset-b",
        "label": "RESET",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "bottom",
        "offset": 0.7
      }
    ],
    "width": 180,
    "height": 130,
    "compatibleVoltages": [
      24
    ],
    "simulateStates": {
      "default": "monitor",
      "options": [
        {
          "id": "monitor",
          "label": "Balance / monitor",
          "intraLinks": []
        }
      ]
    }
  },
  {
    "id": "mon-victron-gx-tank-140",
    "image": "mon-victron-gx-tank-140.webp",
    "name": "Victron GX Tank 140",
    "shortName": "GX Tank 140",
    "subtitle": "USB-powered four-channel 0–10 V / 4–20 mA tank interface",
    "category": "monitor",
    "manufacturer": "Victron",
    "model": "GX Tank 140",
    "terminals": [
      {
        "id": "usb",
        "label": "USB TO GX",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "top",
        "offset": 0.5
      },
      {
        "id": "vin-pos",
        "label": "SENSOR VIN +",
        "polarity": "positive",
        "direction": "input",
        "maxAmp": 0,
        "side": "left",
        "offset": 0.25
      },
      {
        "id": "vin-neg",
        "label": "SENSOR VIN −",
        "polarity": "negative",
        "direction": "input",
        "maxAmp": 0,
        "side": "left",
        "offset": 0.65
      },
      {
        "id": "ch1-supply",
        "label": "CH1 SUPPLY",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "bottom",
        "offset": 0.5714285714285714
      },
      {
        "id": "ch1-in",
        "label": "CH1 IN",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "bottom",
        "offset": 0.7142857142857143
      },
      {
        "id": "ch1-gnd",
        "label": "CH1 GND",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "bottom",
        "offset": 0.8571428571428571
      },
      {
        "id": "ch2-supply",
        "label": "CH2 SUPPLY",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "bottom",
        "offset": 0.14285714285714285
      },
      {
        "id": "ch2-in",
        "label": "CH2 IN",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "bottom",
        "offset": 0.2857142857142857
      },
      {
        "id": "ch2-gnd",
        "label": "CH2 GND",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "bottom",
        "offset": 0.42857142857142855
      },
      {
        "id": "ch3-supply",
        "label": "CH3 SUPPLY",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "right",
        "offset": 0.5714285714285714
      },
      {
        "id": "ch3-in",
        "label": "CH3 IN",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "right",
        "offset": 0.7142857142857143
      },
      {
        "id": "ch3-gnd",
        "label": "CH3 GND",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "right",
        "offset": 0.8571428571428571
      },
      {
        "id": "ch4-supply",
        "label": "CH4 SUPPLY",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "right",
        "offset": 0.14285714285714285
      },
      {
        "id": "ch4-in",
        "label": "CH4 IN",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "right",
        "offset": 0.2857142857142857
      },
      {
        "id": "ch4-gnd",
        "label": "CH4 GND",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "right",
        "offset": 0.42857142857142855
      }
    ],
    "width": 180,
    "height": 130,
    "simulateStates": {
      "default": "monitor",
      "options": [
        {
          "id": "monitor",
          "label": "USB powered",
          "intraLinks": []
        }
      ]
    }
  },
  {
    "id": "mon-blue-sea-m2-1830",
    "image": "mon-blue-sea-m2-1830.webp",
    // M2 DC manual PDF pp. 2–4: included shunt measures Battery 1; pin 8 senses its positive.
    currentShunt: { batteryTerminalId: "batt-neg", systemTerminalId: "load-neg", referenceTerminalId: "sense-pos" },
    "name": "Blue Sea Systems M2 1830 DC SoC Monitor + 8255 Shunt",
    "shortName": "M2 1830 + 8255",
    "subtitle": "State-of-charge monitor with included 500 A / 50 mV negative shunt",
    "category": "monitor",
    "manufacturer": "Blue Sea Systems",
    "model": "M2 1830 + 8255",
    "terminals": [
      {
        "id": "batt-neg",
        "label": "SHUNT BATTERY −",
        "polarity": "negative",
        "direction": "input",
        "maxAmp": 500,
        "side": "left",
        "offset": 0.2
      },
      {
        "id": "load-neg",
        "label": "SHUNT SYSTEM −",
        "polarity": "negative",
        "direction": "output",
        "maxAmp": 500,
        "side": "right",
        "offset": 0.2
      },
      {
        "id": "dc-pos",
        "label": "PIN 2 DC + / BATT 2 +",
        "polarity": "positive",
        "direction": "input",
        "maxAmp": 0,
        "side": "left",
        "offset": 0.65
      },
      {
        "id": "dc-neg",
        "label": "PIN 1 DC −",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "left",
        "offset": 0.9
      },
      {
        "id": "relay-out",
        "label": "PIN 3 RELAY OUT",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0.5,
        "side": "bottom",
        "offset": 0.1
      },
      {
        "id": "relay-supply",
        "label": "PIN 4 RELAY IN / BATT 3 +",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "bottom",
        "offset": 0.35
      },
      {
        "id": "sense-neg",
        "label": "PIN 7 BATTERY 1 −",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "bottom",
        "offset": 0.6
      },
      {
        "id": "sense-pos",
        "label": "PIN 8 BATTERY 1 +",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "bottom",
        "offset": 0.85
      }
    ],
    "width": 180,
    "height": 130,
    "simulateStates": {
      "default": "monitor",
      "options": [
        {
          "id": "monitor",
          "label": "Monitor",
          "intraLinks": [
            [
              "batt-neg",
              "load-neg"
            ]
          ]
        }
      ]
    }
  },
];
